import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import useTransactionAlerts from '@swarm/core/hooks/i18n/useTransactionAlerts'
import usePopupState from '@swarm/core/hooks/state/usePopupState'
import { useExchangeRateOf } from '@swarm/core/observables/exchangeRateOf'
import { useTotalSupplyOf } from '@swarm/core/observables/totalSupplyOf'
import { truncateStringInTheMiddle } from '@swarm/core/shared/utils'
import {
  formatBigInt,
  prettifyBalance,
} from '@swarm/core/shared/utils/formatting'
import { big, normalize } from '@swarm/core/shared/utils/helpers/big-helpers'
import { useAccount } from '@swarm/core/web3'
import { NormalizedStake } from '@swarm/types/normalized-entities'
import SmartButton from '@swarm/ui/swarm/Buttons/SmartButton'
import ExplorerLink from '@swarm/ui/swarm/ExplorerLink'
import { useSnackbar } from '@swarm/ui/swarm/Snackbar'
import TokenIcon from '@swarm/ui/swarm/TokenIcon'
import { BigSource } from 'big.js'
import { TransactionResult } from 'contract-proxy-kit'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Flex, Loader, Text } from 'rimble-ui'
import styled from 'styled-components/macro'
import { layout, LayoutProps } from 'styled-system'

import { SwarmStakingContract } from 'src/contracts/SwarmStakingContract'

import UnstakeSecurityPopup from '../UnstakeSecurityPopup'
import { StakeStatus } from '../consts'
import { calcAPY, getStakeStatusInfo } from '../utils'

const Td = styled.td<LayoutProps>`
  ${layout}
`

interface MyStakeRowProps {
  stake: NormalizedStake
  smtReward?: BigSource
}

const MyStakesRow = ({ stake, smtReward }: MyStakeRowProps) => {
  const { id: stakeId, stakedAmount, stakedTx, stakedSecurity } = stake

  const { t } = useTranslation(['staking', 'errors'])
  const { addError } = useSnackbar()
  const { track } = useTransactionAlerts()
  const { nativeSmt } = useNativeSmt()
  const account = useAccount()

  const totalSupply = useTotalSupplyOf(stakedSecurity.id)
  const exchangeRate = useExchangeRateOf(stakedSecurity.id)
  const unstakeSecurityPopup = usePopupState(false)

  const [txLoading, setTxLoading] = useState(false)
  const [tx, setTx] = useState<TransactionResult>()

  const tvl = useMemo(() => {
    if (!exchangeRate || !totalSupply) return '--'
    return `$${formatBigInt(totalSupply?.times(big(exchangeRate)))}`
  }, [exchangeRate, totalSupply])

  const tokenVolume24hs = useMemo(() => {
    const volume = stake.stakedSecurity.volume24hs
    if (!volume) return '--'
    return `$${formatBigInt(volume)}`
  }, [stake])

  const apy = useMemo(() => {
    if (
      !smtReward ||
      !nativeSmt ||
      !stake.stakedSecurity.smtStaked ||
      !nativeSmt.decimals
    )
      return 0
    return calcAPY(
      normalize(smtReward, nativeSmt.decimals),
      normalize(stake.stakedSecurity.smtStaked, nativeSmt.decimals),
    ).toFixed(2)
  }, [nativeSmt, smtReward, stake.stakedSecurity.smtStaked])

  const totalStakedSMT = nativeSmt?.decimals
    ? formatBigInt(normalize(stakedSecurity.smtStaked, nativeSmt?.decimals))
    : '--'
  const myStakedSMT = nativeSmt?.decimals
    ? prettifyBalance(normalize(stakedAmount, nativeSmt?.decimals))
    : '--'

  const { status, days } = useMemo(() => {
    return getStakeStatusInfo(stake.stakedAt)
  }, [stake.stakedAt])

  const handleExitStake = useCallback(async () => {
    if (!account) {
      return
    }

    try {
      setTxLoading(true)
      const transaction = await SwarmStakingContract.exitStaking(stake, account)

      await track(transaction, {
        confirm: {
          secondaryMessage: 'Staking exited succesffully',
        },
      })

      setTx(transaction)
      unstakeSecurityPopup.open()
    } catch (e) {
      addError(e as Error)
    } finally {
      setTxLoading(false)
    }
  }, [account, stake, track, unstakeSecurityPopup, addError])

  return (
    <tr title={stakeId}>
      <Td>
        <ExplorerLink
          type="tx"
          hash={stakedTx}
          label={truncateStringInTheMiddle(stakedTx)}
        />
      </Td>
      <Td title={`${stakedSecurity.name} (${stakedSecurity.symbol})`}>
        <Flex>
          <TokenIcon
            width="25px"
            height="25px"
            symbol={stakedSecurity.symbol}
            name={stakedSecurity.name}
            maxWidth="none"
          />
          <Text color="near-black" fontWeight="bold" ml={1}>
            {stakedSecurity.symbol}
          </Text>
        </Flex>
      </Td>
      <Td style={{ textAlign: 'center' }}>{tvl}</Td>
      <Td style={{ textAlign: 'center' }}>{tokenVolume24hs}</Td>
      <Td style={{ textAlign: 'center' }}>{totalStakedSMT}</Td>
      <Td style={{ textAlign: 'center' }}>{apy}%</Td>
      <Td style={{ textAlign: 'center' }}>{myStakedSMT}</Td>
      <Td style={{ textAlign: 'center', lineHeight: 1.5 }}>
        <Trans
          ns="staking"
          i18nKey={
            status === StakeStatus.warmup
              ? 'stakeStatus.warmUp'
              : 'stakeStatus.earning'
          }
          components={{ br: <br /> }}
          values={{
            days,
          }}
        />
      </Td>
      <Td>
        <SmartButton
          onClick={handleExitStake}
          lineHeight="20px"
          height="40px"
          width="100px"
          fontSize="14px"
          mr="16px"
          px={['4px', '16px']}
          disabled={txLoading}
          loadingText={<Loader color="white" />}
        >
          {t('actions.unstake')}
        </SmartButton>
      </Td>
      <UnstakeSecurityPopup
        isOpen={unstakeSecurityPopup.isOpen}
        onClose={unstakeSecurityPopup.close}
        stake={stake}
        tx={tx}
      />
    </tr>
  )
}

export default MyStakesRow
